import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { ROUTES } from '@/config/routes';
import { useAuth } from '@/features/auth/session';
import { AppLoader } from '@/shared/components/feedback/AppLoader';
import { PageScrollButtons } from '@/shared/components/PageScrollButtons';
import { HeaderSlotProvider } from '@/shared/components/HeaderSlot';
import { SkipToContent } from '@/shared/components/SkipToContent';
import { cn } from '@/shared/lib/cn';
import { useUiScale } from '@/shared/hooks/useUiScale';

import { AppFooter } from './components/AppFooter';
import { AppSidebar } from './components/AppSidebar';
import { AppTopBar } from './components/AppTopBar';

/**
 * Screens that fill the whole window down to its bottom edge, like a chat, and so have no footer under
 * them. The assistant keeps its composer at the foot of the window, as the previous product did.
 */
const FOOTERLESS_PATHS: readonly string[] = [ROUTES.aiAssistant];

/**
 * The signed-in application's shell: the rail, the bar, and the page between.
 *
 * <h2>Nothing decides what may be opened</h2>
 *
 * The rail used to draw a padlock from `GET /api/v1/account/products`, refreshed
 * as somebody navigated. That endpoint went with plans and subscriptions, so the
 * menu offers every row and the server refuses nothing on the grounds of a
 * product.
 *
 * <p>When something takes its place, the two rules it replaced are worth having
 * back: render an unavailable product rather than hiding it, since hiding one
 * makes it invisible to whoever might buy it; and err towards unlocked while the
 * answer is in flight, because a menu that padlocks everything for a moment on
 * every page load reads as "you have bought nothing" to somebody who has.
 *
 * <h2>The sidebar is fixed, so the content is pushed rather than flowed</h2>
 *
 * Both the rail and the bar are `position: fixed`, which takes them out of flow
 * — hence the left margin and top padding here. It is what lets the page scroll
 * under a bar that stays put, and what makes the collapse animate rather than
 * reflow the document.
 */
export function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const { pathname } = useLocation();

  // Through the session context rather than a fetch of its own. Every screen needs
  // to know who is signed in, and each one asking in its own way is how a header
  // and a menu end up disagreeing about it.
  const { user } = useAuth();

  // Publishes `--ui-scale` for the shell. Here rather than in the top bar, which is the one
  // thing reading it today: the variable describes the window and the layout is what owns
  // the window, so a second consumer -- a page drawn in the design's pixel sizes -- adds a
  // `zoom` and nothing else.
  useUiScale();

  /*
   * Keeps the page's scrollbar track while a menu or select is open.
   *
   * Radix locks the page's scroll while one is open: the scrollbar vanishes and the library pads the
   * page by its width to hide the difference. `scrollbar-gutter: stable` already reserves that space,
   * so the padding made the page shrink and everything flinch on open and again on close.
   * `html.app-layout-scroll` in `index.css` holds the track in place and cancels the padding; the
   * class is only ever wanted inside this shell, so it is added here and removed on leaving.
   */
  useEffect(() => {
    document.documentElement.classList.add('app-layout-scroll');
    return () => {
      document.documentElement.classList.remove('app-layout-scroll');
    };
  }, []);

  /*
   * The element the top bar keeps beside the breadcrumb, for a screen that owns a header
   * control. State rather than a ref: it goes into a context, and React has to re-render
   * for the screens below to see it arrive. Null until the bar's first commit, which
   * `HeaderSlot` treats as "nowhere to portal to yet" rather than as an error.
   */
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null);

  /*
   * How many screens are currently using the header slot — in practice 0 or 1.
   *
   * The bar shows a fallback control when nothing has filled it, so this is the question
   * "has the screen brought its own", which the DOM cannot be asked without reading it
   * after every render. The design counts the same way (`AppNav.tsx:1588`).
   */
  const [headerSlotUsers, setHeaderSlotUsers] = useState(0);

  const countHeaderSlotUser = useCallback((delta: number) => {
    setHeaderSlotUsers((users) => users + delta);
  }, []);

  /*
   * Memoised, because it is a context value: a fresh object each render would re-run the
   * registering effect in every `HeaderSlot` below, which counts up and down forever.
   */
  const headerSlotContext = useMemo(
    () => ({ target: headerSlot, countUser: countHeaderSlotUser }),
    [headerSlot, countHeaderSlotUser],
  );

  // Empty until /account/me answers, which hides the restricted rows for that
  // moment. See AppSidebar#isVisible for why that is the right direction.
  //
  // Both come off the one session read. The rail filters on both -- roles decide Team and
  // the operator's rows, products decide the six product rows -- and the top bar on roles
  // alone. Asking twice is how a header and a menu end up disagreeing about who is signed in.
  const roleKeys = new Set(user?.roles ?? []);
  // Derived here rather than kept in state: `products` is the one representation and its
  // key set is the membership, so a stored copy would be the duplicate PP-31 removed.
  const productKeys = new Set(Object.keys(user?.products ?? {}));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-[#0a1428] dark:to-[#0c182d]">
      <SkipToContent />

      <AppSidebar
        collapsed={collapsed}
        onToggle={() => {
          setCollapsed((open) => !open);
        }}
        roleKeys={roleKeys}
        productKeys={productKeys}
        user={user}
      />

      <div
        className={cn(
          'flex min-h-screen flex-col transition-all duration-500 ease-out',
          // The rail's own classes, not pixels: `--spacing` is rem and this root font is
          // 14px, so these are 61.25px and 227.5px. See `AppSidebar`.
          collapsed ? 'lg:ml-17.5' : 'lg:ml-65',
        )}
      >
        <AppTopBar
          sidebarCollapsed={collapsed}
          roleKeys={roleKeys}
          // The mobile drawer is not built. Collapsing the rail is the nearest
          // honest behaviour, and it keeps the control from being a dead button.
          onOpenMobileMenu={() => {
            setCollapsed((open) => !open);
          }}
          onSlotReady={setHeaderSlot}
          headerSlotFilled={headerSlotUsers > 0}
        />

        {/* `flex flex-col` so a screen can fill the region with `flex-1` instead of guessing
            at it with viewport maths. An ordinary page is a single child that does not set
            `flex-1`, so it keeps its natural height exactly as before -- the only thing this
            adds is the ability to ask. */}
        <main id="main" className="flex min-w-0 flex-1 flex-col pt-16">
          <Suspense fallback={<AppLoader />}>
            {/* Around the Outlet, so only a routed screen may fill the header's slot. */}
            <HeaderSlotProvider value={headerSlotContext}>
              <Outlet />
            </HeaderSlotProvider>
          </Suspense>
        </main>

        {/*
          Outside `<main>`, not inside it: a `<footer>` nested in the main landmark is what
          "skip to content" exists to get past. `<main>` is `flex-1`, so a short page still
          pushes this to the bottom.
        */}
        {!FOOTERLESS_PATHS.includes(pathname) && <AppFooter />}

        {/*
          Here, not on the screen that asked for it. "Take me back to the top" belongs to
          any page long enough to lose your place in, and the Team list is only the first
          of those -- built on one screen it would be rebuilt on the next, and the copies
          would disagree. It renders nothing at all on a page that does not scroll.
        */}
        <PageScrollButtons />
      </div>
    </div>
  );
}
